import { useState, useEffect } from "react";
import {
  Bell,
  MessageCircle,
  DollarSign,
  UserPlus,
  FileText,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { formatDistanceToNow } from "date-fns";

const iconMap = {
  message: MessageCircle,
  payment: DollarSign,
  team: UserPlus,
  request: FileText,
  job: CheckCircle,
  alert: AlertCircle,
  job_completed: CheckCircle,
  new_quote: FileText,
  comment: MessageCircle,
};

const colorMap = {
  message: "from-blue-500 to-blue-600",
  payment: "from-green-500 to-emerald-600",
  team: "from-purple-500 to-purple-600",
  request: "from-orange-500 to-orange-600",
  job: "from-cyan-500 to-cyan-600",
  alert: "from-red-500 to-rose-600",
  job_completed: "from-cyan-500 to-cyan-600",
  new_quote: "from-orange-500 to-orange-600",
  comment: "from-pink-500 to-pink-600",
};

export default function NotificationButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await api.get("/company/notifications/unread-count");
        setUnreadCount(res.data.unread_count || 0);
      } catch (err) {
        console.error("Failed to fetch unread count", err);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get("/company/notifications?is_read=false");
      const data = res.data.data || [];

      const formatted = data.map((notif) => ({
        id: notif.id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        time: formatDistanceToNow(new Date(notif.created_at), {
          addSuffix: true,
        }),
        color: colorMap[notif.type] || "from-gray-500 to-gray-600",
        icon: iconMap[notif.type] || Bell,
      }));

      setNotifications(formatted);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  // useEffect(() => {
  //     if (isOpen && unreadCount > 0) {
  //         api.put('/company/notifications/mark-all-read')
  //             .then(() => setUnreadCount(0))
  //             .catch(console.error);
  //     }
  // }, [isOpen, unreadCount]);

  return (
    <div className="relative inline-block">
      <button
        onClick={handleToggle}
        className={`
          relative w-10 h-10 rounded-lg flex items-center justify-center
          transition-all duration-300 group
          ${isOpen ? "bg-gray-200 ring-4 ring-gray-300/50" : "hover:bg-gray-100"}
        `}
      >
        <Bell
          className={`
          w-5 h-5 transition-all duration-300
          ${isOpen ? "text-gray-800 rotate-90" : "text-gray-600 group-hover:text-gray-800"}
        `}
        />

        {unreadCount > 0 && !isOpen && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          <div className="absolute right-0 top-full mt-3 w-96 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Notifications</h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await api.put("/company/notifications/read-all");
                          setUnreadCount(0);
                          setNotifications([]);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded cursor-pointer transition-colors"
                    >
                      Mark all read
                    </button>
                  )}
                  <span className="text-sm bg-white/20 px-3 py-1 rounded-full">
                    {unreadCount} unread
                  </span>
                </div>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No new notifications
                </div>
              ) : (
                notifications.map((notif, i) => {
                  const Icon = notif.icon;
                  return (
                    <div
                      key={notif.id}
                      className="group flex items-center gap-4 p-4 hover:bg-gray-50 transition-all border-b border-gray-100 last:border-0"
                      style={{ animationDelay: `${i * 50}ms` }}
                    >
                      <div
                        className={`w-11 h-11 rounded-xl bg-gradient-to-br ${notif.color} flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all`}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>

                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-800 text-sm">
                          {notif.title}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {notif.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {notif.time}
                        </p>
                      </div>

                      <div className="w-2 h-2 bg-blue-500 rounded-full opacity-80"></div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="border-t border-gray-100 p-4 text-center">
              <Link
                to="/profile/notification"
                onClick={() => setIsOpen(false)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                View all notifications →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
